import { primitives } from './primitives';
import { semantic } from './semantic';

/** Layer 3 — Component-scoped tokens. */
export const componentTokens = {
  card: {
    bg: semantic.color.surface.raised,
    border: semantic.color.border.subtle,
    radius: primitives.radius['2xl'],
    shadow: primitives.shadow.card,
    shadowHover: primitives.shadow.cardHover,
    padding: semantic.space.cardPadding,
    imageRadius: primitives.radius['2xl'],
    transform: 'translateY(-2px)',
    transition: `box-shadow ${primitives.duration.normal} ${primitives.easing.easeOut}, transform ${primitives.duration.normal} ${primitives.easing.easeOut}`,
  },
  button: {
    primary: {
      bg: semantic.color.brand.primary,
      bgHover: primitives.color.brand[600],
      bgActive: primitives.color.brand[800],
      text: semantic.color.text.inverse,
      radius: primitives.radius.lg,
      paddingX: primitives.space[6],
      paddingY: primitives.space[3],
      fontWeight: primitives.fontWeight.semibold,
      fontSize: primitives.fontSize.base,
    },
    secondary: {
      bg: 'transparent',
      bgHover: semantic.color.surface.overlay,
      border: semantic.color.border.strong,
      text: semantic.color.text.primary,
      radius: primitives.radius.lg,
    },
    ghost: {
      bg: 'transparent',
      bgHover: semantic.color.surface.overlay,
      text: semantic.color.text.secondary,
      radius: primitives.radius.lg,
    },
    danger: {
      bg: primitives.color.red[500],
      bgHover: primitives.color.red[700],
      text: primitives.color.neutral[0],
      radius: primitives.radius.lg,
    },
    /**
     * Filter / category chips (Airbnb-like): restrained radius, neutral idle state,
     * brand emphasis fill when selected — avoids “toy” full-saturation pills sitewide.
     */
    filterChip: {
      radius: primitives.radius.lg,
      minHeight: '44px',
      paddingX: primitives.space[4],
      paddingY: primitives.space[2],
      gap: primitives.space[2],
      fontSize: primitives.fontSize.sm,
      fontWeightInactive: primitives.fontWeight.medium,
      fontWeightActive: primitives.fontWeight.semibold,
      shadow: primitives.shadow.xs,
      shadowHover: primitives.shadow.sm,
      inactiveBg: semantic.color.surface.raised,
      inactiveBorder: semantic.color.border.default,
      inactiveText: semantic.color.text.primary,
      inactiveHoverBg: semantic.color.surface.overlay,
      inactiveHoverBorder: semantic.color.border.strong,
      activeBg: semantic.color.brand.emphasis,
      activeBorder: semantic.color.brand.emphasis,
      activeText: semantic.color.brand.onPrimary,
      activeHoverBg: primitives.color.brand[900],
      activeHoverBorder: primitives.color.brand[900],
      focusTransition: `background-color ${primitives.duration.fast} ${primitives.easing.easeOut}, border-color ${primitives.duration.fast} ${primitives.easing.easeOut}, color ${primitives.duration.fast} ${primitives.easing.easeOut}, box-shadow ${primitives.duration.fast} ${primitives.easing.easeOut}`,
    },
    /** Admin range date toggles — compact, no full radius, semantic tokens throughout */
    rangeToggle: {
      radius: primitives.radius.sm,
      paddingX: primitives.space[3],
      paddingY: primitives.space[1],
      fontSize: primitives.fontSize.sm,
      fontWeight: primitives.fontWeight.medium,
      inactiveBg: semantic.color.surface.raised,
      inactiveBorder: semantic.color.border.default,
      inactiveText: semantic.color.text.secondary,
      inactiveHoverBg: semantic.color.surface.sunken,
      activeBg: semantic.color.brand.primary,
      activeBorder: semantic.color.brand.primary,
      activeText: semantic.color.brand.onPrimary,
      activeHoverBg: semantic.color.brand.primaryHover,
      transition: `background-color ${primitives.duration.fast} ${primitives.easing.ease}, color ${primitives.duration.fast} ${primitives.easing.ease}, border-color ${primitives.duration.fast} ${primitives.easing.ease}`,
    },
  },
  /** Carousel / slider slide indicator dots */
  dot: {
    size: primitives.space[3],
    radiusFull: primitives.radius.full,
    darkInactive: primitives.color.neutral[200],
    darkActive: semantic.color.brand.primary,
    darkHover: semantic.color.brand.primaryHover,
    lightInactive: 'rgba(255,255,255,0.45)',
    lightActive: primitives.color.neutral[0],
    lightHover: primitives.color.neutral[0],
    transition: `background-color ${primitives.duration.normal} ${primitives.easing.ease}, transform ${primitives.duration.normal} ${primitives.easing.ease}`,
  },
  /** Carousel prev/next navigation arrow buttons */
  carouselNav: {
    size: primitives.space[10],
    radius: primitives.radius.full,
    framedBg: semantic.color.surface.raised,
    framedBorder: semantic.color.border.default,
    framedText: semantic.color.text.primary,
    framedHoverBg: semantic.color.surface.overlay,
    framedHoverBorder: semantic.color.border.strong,
    filledBg: semantic.color.brand.primary,
    filledText: semantic.color.brand.onPrimary,
    filledHoverBg: semantic.color.brand.primaryHover,
    disabledOpacity: '0.4',
    transition: `background-color ${primitives.duration.fast} ${primitives.easing.easeOut}, border-color ${primitives.duration.fast} ${primitives.easing.easeOut}, transform ${primitives.duration.fast} ${primitives.easing.easeOut}`,
  },
  input: {
    bg: semantic.color.surface.sunken,
    border: semantic.color.border.default,
    borderFocus: semantic.color.border.focus,
    borderError: semantic.color.feedback.error,
    radius: primitives.radius.md,
    text: semantic.color.text.primary,
    placeholder: semantic.color.text.tertiary,
    paddingX: primitives.space[4],
    paddingY: primitives.space[3],
    focusRing: semantic.focusRing,
    transition: `border-color ${primitives.duration.fast} ${primitives.easing.ease}, box-shadow ${primitives.duration.fast} ${primitives.easing.ease}`,
  },
  /** Public trip detail — «Programa» accordion (day headers + content inset) */
  itineraryAccordion: {
    dayHeaderPaddingY: primitives.space[4],
    dayHeaderPaddingX: primitives.space[6],
    dayHeaderPaddingYMobile: primitives.space[4],
    dayHeaderPaddingXMobile: primitives.space[5],
    contentPaddingX: primitives.space[8],
    contentPaddingBottom: primitives.space[8],
    contentPaddingXMobile: primitives.space[6],
    contentPaddingBottomMobile: primitives.space[6],
  },
  tripCard: {
    imageAspectRatio: '75%',
    imageRadius: primitives.radius['2xl'],
    textGap: primitives.space[2],
    titleWeight: primitives.fontWeight.semibold,
    titleSize: primitives.fontSize.base,
    metaColor: semantic.color.text.secondary,
    metaSize: primitives.fontSize.sm,
    priceColor: semantic.color.text.primary,
  },
  hero: {
    minHeight: 'min(600px, 80vh)',
    imageObjectFit: 'cover' as const,
    scrim: semantic.scrim.hero,
    titleColor: primitives.color.neutral[0],
    subtitleColor: 'rgba(255,255,255,0.85)',
  },
  table: {
    headerBg: semantic.color.surface.overlay,
    headerText: semantic.color.text.secondary,
    headerFontSize: primitives.fontSize.xs,
    headerWeight: primitives.fontWeight.semibold,
    headerTracking: primitives.letterSpacing.wide,
    rowBg: semantic.color.surface.raised,
    rowHoverBg: semantic.color.surface.page,
    border: semantic.color.border.subtle,
    cellPaddingX: primitives.space[4],
    cellPaddingY: primitives.space[3],
    fontSize: primitives.fontSize.sm,
  },
  badge: {
    success: {
      bg: semantic.color.feedback.successBg,
      text: semantic.color.feedback.successText,
    },
    warning: {
      bg: semantic.color.feedback.warningBg,
      text: semantic.color.feedback.warningText,
    },
    error: {
      bg: semantic.color.feedback.errorBg,
      text: semantic.color.feedback.errorText,
    },
    info: {
      bg: semantic.color.feedback.infoBg,
      text: semantic.color.feedback.infoText,
    },
    neutral: {
      bg: semantic.color.surface.overlay,
      text: semantic.color.text.secondary,
    },
    radius: primitives.radius.full,
    paddingX: primitives.space[2],
    paddingY: '2px',
    fontSize: primitives.fontSize.xs,
    fontWeight: primitives.fontWeight.semibold,
  },
  /**
   * `viagens.status` — `palette.*.solid` for public pills; `palette.*.surface` for dense admin controls (e.g. list dropdown).
   * Copy comes from `constants/viagemStatus`, not hardcoded in consumers.
   */
  viagemStatus: {
    palette: {
      brevemente: {
        solid: {
          bg: semantic.color.brand.primary,
          fg: semantic.color.brand.onPrimary,
        },
        surface: {
          bg: semantic.color.feedback.infoBg,
          fg: semantic.color.feedback.infoText,
        },
      },
      ultimos_lugares: {
        solid: {
          bg: primitives.color.amber[500],
          fg: primitives.color.amber[900],
        },
        surface: {
          bg: semantic.color.feedback.warningBg,
          fg: semantic.color.feedback.warningText,
        },
      },
      disponivel: {
        solid: {
          bg: semantic.color.feedback.success,
          fg: semantic.color.brand.onPrimary,
        },
        surface: {
          bg: semantic.color.feedback.successBg,
          fg: semantic.color.feedback.successText,
        },
      },
      esgotado: {
        solid: {
          bg: semantic.color.feedback.error,
          fg: semantic.color.brand.onPrimary,
        },
        surface: {
          bg: semantic.color.feedback.errorBg,
          fg: semantic.color.feedback.errorText,
        },
      },
    },
    badgeCompact: {
      fontSize: primitives.fontSize.xs,
      paddingY: primitives.space[2],
      paddingX: semantic.space.md,
      borderRadius: primitives.radius.full,
      fontWeight: primitives.fontWeight.semibold,
      letterSpacing: primitives.letterSpacing.wide,
      lineHeight: primitives.lineHeight.snug,
    },
    badgeComfortable: {
      fontSize: primitives.fontSize.sm,
      paddingY: semantic.space.sm,
      paddingX: semantic.space.md,
      borderRadius: primitives.radius.xl,
      fontWeight: primitives.fontWeight.semibold,
      letterSpacing: primitives.letterSpacing.wide,
      lineHeight: primitives.lineHeight.snug,
    },
  },
  admin: {
    sidebarWidth: '280px',
    sidebarWidthCollapsed: '68px',
    headerHeight: '64px',
    navActiveBorder: `3px solid ${primitives.color.brand[500]}`,
    modalZIndex: 1000,
    modalMaxWidth: '600px',
    filterMinWidth: '200px',
    dropdownMinWidth: '160px',
    /** Single max width for admin main column (header + page body); aligns with AdminLayout shell */
    contentMaxWidth: '1400px',
    /** Horizontal inset for admin column; aliases semantic.page gutter */
    contentPaddingX: semantic.space.lg,
  },
} as const;

export type ComponentTokens = typeof componentTokens;
